import { ServerMessage, MODULES, SHIP_CLASSES, ORES } from '@deepwake/shared';

type StationInfo = Extract<ServerMessage, { t: 'stationInfo' }>;

export interface StationCallbacks {
  send: (m: unknown) => void;
}

/** Docked station screen: hangar, fitting, refine, foundry, market, missions. */
export class StationScreen {
  root: HTMLElement;
  info: StationInfo | null = null;
  tab = 'hangar';

  constructor(root: HTMLElement, private cb: StationCallbacks) {
    this.root = root;
  }

  show(info: StationInfo): void {
    this.info = info;
    this.root.classList.remove('hidden');
    this.render();
  }

  hide(): void {
    this.root.classList.add('hidden');
    this.info = null;
  }

  private render(): void {
    const info = this.info;
    if (!info) return;
    const tabs = ['hangar', 'fitting', 'refine', 'foundry', 'market', 'missions'];
    this.root.innerHTML = `
      <div class="stn-head">
        <div>
          <div class="ptitle">${esc(info.name)}</div>
          <div class="sub">${esc(info.faction)} · ${info.marks.toLocaleString()} Marks · flying: ${esc(SHIP_CLASSES[info.shipClass]?.name ?? info.shipClass)}</div>
        </div>
        <div>
          ${tabs.map((t) => `<button class="tab ${t === this.tab ? 'primary' : ''}" data-tab="${t}">${t}</button>`).join('')}
          <button id="stn-forum">Forum</button>
          <button id="stn-undock" class="primary">Undock</button>
        </div>
      </div>
      <div class="stn-body" id="stn-body"></div>`;
    this.root.querySelectorAll<HTMLButtonElement>('button.tab').forEach((b) =>
      b.addEventListener('click', () => {
        this.tab = b.dataset.tab!;
        this.render();
      }),
    );
    this.root.querySelector('#stn-undock')!.addEventListener('click', () => this.cb.send({ t: 'undock' }));
    this.root.querySelector('#stn-forum')!.addEventListener('click', () => window.open('/forum.html', '_blank'));

    const body = this.root.querySelector('#stn-body') as HTMLElement;
    const r = (html: string) => (body.innerHTML = html);

    if (this.tab === 'hangar') {
      const items = Object.entries(info.hangar.items).filter(([, n]) => n > 0);
      const cargo = Object.entries(info.cargo).filter(([, n]) => n > 0);
      r(`
        <div class="cols">
          <div><h3>Hangar</h3>${itemTable(items, 'toCargo')}</div>
          <div><h3>Ship cargo</h3>${itemTable(cargo, 'toHangar')}</div>
          <div><h3>Hulls</h3>${
            info.hangar.ships.length
              ? info.hangar.ships
                  .map(
                    (s, i) =>
                      `<div class="row"><span>${esc(SHIP_CLASSES[s]?.name ?? s)}</span><button data-switch="${esc(s)}">Board</button></div>`,
                  )
                  .join('')
              : '<div class="sub">No spare hulls here</div>'
          }</div>
        </div>`);
      body.querySelectorAll<HTMLButtonElement>('button[data-move]').forEach((b) =>
        b.addEventListener('click', () => {
          const qty = Number(prompt(`How many ${b.dataset.item}?`, '100')) || 0;
          if (qty > 0) this.cb.send({ t: 'transfer', item: b.dataset.item, qty, dir: b.dataset.move });
        }),
      );
      body.querySelectorAll<HTMLButtonElement>('button[data-switch]').forEach((b) =>
        b.addEventListener('click', () => this.cb.send({ t: 'switchShip', classKey: b.dataset.switch })),
      );
    }

    if (this.tab === 'fitting') {
      const cls = SHIP_CLASSES[info.shipClass];
      const owned = Object.entries(info.hangar.items).filter(([k, n]) => n > 0 && MODULES[k]);
      r(`
        <div class="cols">
          <div>
            <h3>Fitted (${cls.slots.weapon}W / ${cls.slots.system}S / ${cls.slots.rig}R)</h3>
            <div id="fitlist">${info.fitted
              .map(
                (k, i) =>
                  `<div class="row"><span>[${MODULES[k]?.slot[0].toUpperCase()}] ${esc(MODULES[k]?.name ?? k)}</span><button data-unfit="${i}">Unfit</button></div>`,
              )
              .join('') || '<div class="sub">Nothing fitted</div>'}</div>
          </div>
          <div>
            <h3>Modules in hangar</h3>
            ${owned
              .map(
                ([k, n]) =>
                  `<div class="row"><span>[${MODULES[k].slot[0].toUpperCase()}] ${esc(MODULES[k].name)} ×${n}</span><button data-fit="${esc(k)}">Fit</button></div>`,
              )
              .join('') || '<div class="sub">Buy or build modules first</div>'}
          </div>
        </div>`);
      const refit = (mods: string[]) => this.cb.send({ t: 'fit', modules: mods });
      body.querySelectorAll<HTMLButtonElement>('button[data-unfit]').forEach((b) =>
        b.addEventListener('click', () => {
          const mods = [...info.fitted];
          mods.splice(Number(b.dataset.unfit), 1);
          refit(mods);
        }),
      );
      body.querySelectorAll<HTMLButtonElement>('button[data-fit]').forEach((b) =>
        b.addEventListener('click', () => refit([...info.fitted, b.dataset.fit!])),
      );
    }

    if (this.tab === 'refine') {
      const ores = Object.entries(info.hangar.items).filter(([k, n]) => n > 0 && ORES[k]);
      r(`
        <h3>Refinery</h3>
        ${ores
          .map(
            ([k, n]) =>
              `<div class="row"><span>${esc(ORES[k].name)} ×${n} → ${Object.entries(ORES[k].refineTo)
                .map(([m, p]) => `${p} ${m}`)
                .join(' + ')} per 100</span><button data-refine="${esc(k)}" data-qty="${n}">Refine all</button></div>`,
          )
          .join('') || '<div class="sub">No ore in hangar. Mine some, or move it from cargo.</div>'}`);
      body.querySelectorAll<HTMLButtonElement>('button[data-refine]').forEach((b) =>
        b.addEventListener('click', () => this.cb.send({ t: 'refine', ore: b.dataset.refine, qty: Number(b.dataset.qty) })),
      );
    }

    if (this.tab === 'foundry') {
      r(`
        <h3>Foundry</h3>
        <div class="cols"><div>${info.blueprints
          .filter((bp) => bp.kind === 'ship')
          .map((bp) => bpRow(bp))
          .join('')}</div><div>${info.blueprints
          .filter((bp) => bp.kind === 'module')
          .map((bp) => bpRow(bp))
          .join('')}</div></div>`);
      body.querySelectorAll<HTMLButtonElement>('button[data-build]').forEach((b) =>
        b.addEventListener('click', () => this.cb.send({ t: 'build', blueprint: b.dataset.build })),
      );
    }

    if (this.tab === 'market') {
      const grouped = new Map<string, typeof info.orders>();
      for (const o of info.orders) {
        if (!grouped.has(o.item)) grouped.set(o.item, []);
        grouped.get(o.item)!.push(o);
      }
      r(`
        <h3>Market</h3>
        <div class="row">
          <input id="mk-item" placeholder="item key (e.g. cindral)"/>
          <input id="mk-qty" type="number" placeholder="qty" value="100"/>
          <input id="mk-price" type="number" placeholder="price"/>
          <button id="mk-buy">Buy</button><button id="mk-sell">Sell</button>
        </div>
        ${[...grouped.entries()]
          .map(([item, orders]) => {
            const asks = orders.filter((o) => o.side === 'sell').slice(0, 3);
            const bids = orders.filter((o) => o.side === 'buy').slice(-3);
            return `<div class="row mk"><b>${esc(item)}</b>
              <span>ask: ${asks.map((o) => `${o.price}${o.own ? '*' : ''}`).join(' / ') || '—'}</span>
              <span>bid: ${bids.map((o) => `${o.price}${o.own ? '*' : ''}`).join(' / ') || '—'}</span>
              ${orders.filter((o) => o.own).map((o) => `<button data-cancel="${o.id}">Cancel ${o.side} ${o.qty}</button>`).join('')}
            </div>`;
          })
          .join('')}`);
      const val = (id: string) => (body.querySelector(id) as HTMLInputElement).value.trim();
      body.querySelector('#mk-buy')!.addEventListener('click', () =>
        this.cb.send({ t: 'order', side: 'buy', item: val('#mk-item'), qty: Number(val('#mk-qty')), price: Number(val('#mk-price')) }),
      );
      body.querySelector('#mk-sell')!.addEventListener('click', () =>
        this.cb.send({ t: 'order', side: 'sell', item: val('#mk-item'), qty: Number(val('#mk-qty')), price: Number(val('#mk-price')) }),
      );
      body.querySelectorAll<HTMLButtonElement>('button[data-cancel]').forEach((b) =>
        b.addEventListener('click', () => this.cb.send({ t: 'cancelOrder', orderId: Number(b.dataset.cancel) })),
      );
    }

    if (this.tab === 'missions') {
      r(`
        <h3>Mission board</h3>
        ${info.missions
          .map(
            (m) => `<div class="mission ${m.accepted ? 'accepted' : ''}">
              <b>${esc(m.title)}</b> <span class="sub">${m.reward.toLocaleString()} Marks</span>
              <p class="sub">${esc(m.desc)}</p>
              ${
                m.accepted
                  ? `${m.complete || m.kind === 'haul' ? `<button data-mcomplete="${esc(m.id)}" class="primary">Complete</button>` : '<span class="sub">In progress…</span>'}
                     <button data-mabandon="${esc(m.id)}">Abandon</button>`
                  : `<button data-maccept="${esc(m.id)}">Accept</button>`
              }
            </div>`,
          )
          .join('')}`);
      body.querySelectorAll<HTMLButtonElement>('button[data-maccept]').forEach((b) =>
        b.addEventListener('click', () => this.cb.send({ t: 'missions', action: 'accept', id: b.dataset.maccept })),
      );
      body.querySelectorAll<HTMLButtonElement>('button[data-mcomplete]').forEach((b) =>
        b.addEventListener('click', () => this.cb.send({ t: 'missions', action: 'complete', id: b.dataset.mcomplete })),
      );
      body.querySelectorAll<HTMLButtonElement>('button[data-mabandon]').forEach((b) =>
        b.addEventListener('click', () => this.cb.send({ t: 'missions', action: 'abandon', id: b.dataset.mabandon })),
      );
    }
  }
}

function itemTable(items: [string, number][], moveDir: string): string {
  if (!items.length) return '<div class="sub">Empty</div>';
  return items
    .map(
      ([k, n]) =>
        `<div class="row"><span>${esc(label(k))} ×${n}</span><button data-move="${moveDir}" data-item="${esc(k)}">${moveDir === 'toCargo' ? '→ cargo' : '→ hangar'}</button></div>`,
    )
    .join('');
}

function bpRow(bp: { key: string; kind: string; cost: Record<string, number>; fee: number; locked: boolean }): string {
  const name = bp.kind === 'ship' ? SHIP_CLASSES[bp.key]?.name : MODULES[bp.key]?.name;
  const cost = Object.entries(bp.cost)
    .map(([m, n]) => `${n} ${m}`)
    .join(' + ');
  return `<div class="row ${bp.locked ? 'locked' : ''}"><span>${esc(name ?? bp.key)} <span class="sub">(${cost} + ${bp.fee} Marks)</span></span>
    ${bp.locked ? '<span class="sub">mastery locked</span>' : `<button data-build="${esc(bp.key)}">Build</button>`}</div>`;
}

function label(k: string): string {
  return ORES[k]?.name ?? MODULES[k]?.name ?? k;
}

function esc(s: string): string {
  return s.replace(/[&<>"']/g, (c) => `&#${c.charCodeAt(0)};`);
}
